import React, { useRef, useState } from 'react'
import { AnimatedContentLoader, Button, Input, InputProps, ModalProps, ModalV2 } from '..'
import { collectFromPages, PaginatedResponse } from '@root/query'
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { FaPlus, FaTimes } from 'react-icons/fa'

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import { useDebouncedValue, useVirtualList } from '@root/hooks'
import { useTranslation } from 'react-i18next'
import theme from '@root/styles/theme'
import { useScreen } from '@root/utils'
import { shift } from '@floating-ui/react-dom'
import { Spinner } from '@components/ui/Primitives'

const MAX_HEIGHT_DROPDOWN = 300
// Prevent refetching results for 5 mins unless query invalidated on purpose
const QUERY_STALE_TIME = 15 * 60 * 1000 // 15 mins

export interface ItemRenderProps<T> {
  isFocused: boolean
  isSelected: boolean
  onKeyDown: (e: any) => void
  handleOnItemClick: (item: T) => void
  handleOnEditClick: (item: T) => void
  style: React.CSSProperties
  showEditItem: boolean
  isFirstOfType: boolean
  isLastOfType: boolean
}

export interface SuggestionRenderProps<T> {
  handleOnItemClick: (item: T) => void
}

export interface FetchItemsProps {
  search?: string
  page: number
}

type RenderFunctionType = (onCreate: OnCreateType, item: any) => React.ReactNode
type OnCreateType = (data: any) => any

export interface UniversalAPISelectorProps<T> extends InputProps {
  dropdownHeaderIcon?: React.ReactElement
  dropDownHeader: string

  getItem: (id: number) => Promise<T>
  getItemQueryKeyFn: (id: number) => (string | number | Record<any, any>)[]
  getItems: (props: FetchItemsProps) => Promise<PaginatedResponse<T>>
  getItemsQueryKeyFn: (filters?: Record<any, any>) => (string | number | Record<any, any>)[]

  renderRow: (item: T, props: ItemRenderProps<T>) => React.ReactNode
  itemSize?: number
  renderUpdateForm?: RenderFunctionType
  renderCreateForm?: RenderFunctionType
  renderQuickCreate?: (
    searchTerm: string,
    isFocused: boolean,
    handleItemClick: (item: T) => void
  ) => React.ReactNode
  handleQuickCreate?: (searchTerm: string, handleItemClick: (item: T) => void) => void

  getDataToShow: (item: T) => string

  value?: number
  onChange: (itemId: number) => void

  modalHeaderNew?: string
  modalHeaderUpdate?: string
  formId?: string

  noDataMessage: string | React.ReactNode

  createModalProps?: Partial<ModalProps>
  updateModalProps?: Partial<ModalProps>

  fetchSuggestion?: () => Promise<any>
  renderSuggestion?: (item: T, isEnabled, porps: SuggestionRenderProps<T>) => React.ReactNode
  isSuggestionEnabled?: boolean
  renderEmpty?: (helpers: { openCreateModal: () => void }) => React.ReactNode
}

const UniversalAPISelector: React.FC<UniversalAPISelectorProps<any>> = ({
  getItem,
  getItemQueryKeyFn,
  getItems,
  getItemsQueryKeyFn,
  renderRow,
  itemSize = 35,
  id,
  name,
  noDataMessage,
  getDataToShow,
  renderCreateForm,
  renderUpdateForm,
  renderQuickCreate,
  handleQuickCreate,
  onChange,
  value: itemId,
  modalHeaderNew,
  modalHeaderUpdate,
  dropdownHeaderIcon,
  dropDownHeader,
  formId,
  createModalProps,
  updateModalProps,
  fetchSuggestion,
  renderSuggestion,
  isSuggestionEnabled: _isSuggestionEnabled,
  renderEmpty,
  ...rest
}) => {
  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))
  const [t] = useTranslation()
  const { isMobile } = useScreen()
  const queryClient = useQueryClient()

  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const [isCreateModalOpen, setCreateModalOpen] = useState(false)
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false)
  const {
    debouncedValue: debouncedSearch,
    pendingValue: searchTerm,
    setValue: setSearch,
    setImmediateValue: setImmediateSearchTerm
  } = useDebouncedValue<string>({ delay: 300, initialValue: '' })

  const isSuggestionEnabled = !!fetchSuggestion && !!renderSuggestion && _isSuggestionEnabled

  const {
    data,
    isLoading: isItemsLoading,
    isFetching: isItemsFetching,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: getItemsQueryKeyFn({ search: debouncedSearch }),
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => getItems({ page: pageParam, search: debouncedSearch }),
    getNextPageParam: lastPage => lastPage.next,
    getPreviousPageParam: lastPage => lastPage.prev,
    staleTime: QUERY_STALE_TIME,
    placeholderData: keepPreviousData,
    enabled: isDropdownOpen // Enable only when the dropdown is open
  })
  const allItems = collectFromPages(data)
  const isSearchTermEmpty = !searchTerm

  const useQuickAddItem =
    typeof renderQuickCreate === 'function' &&
    typeof handleQuickCreate === 'function' &&
    !isSearchTermEmpty

  const allItemsLength = useQuickAddItem ? allItems.length : allItems.length - 1
  const noResults = allItems?.length === 0

  const result = useQuery({
    queryKey: getItemQueryKeyFn(itemId),
    queryFn: () => getItem(itemId),
    ...{
      enabled: !!itemId,
      staleTime: QUERY_STALE_TIME
    }
  })

  const { data: suggestion } = useQuery({
    queryKey: [...getItemQueryKeyFn(itemId), 'suggestion'],
    queryFn: () => fetchSuggestion?.(),
    ...{
      enabled: isSuggestionEnabled,
      staleTime: QUERY_STALE_TIME
    }
  })

  const { context, x, y, strategy, refs } = useFloating({
    open: isDropdownOpen,
    onOpenChange: open => {
      // When dropdown is closed, reset the search term to the selected item
      const item = allItems.find(item => item.id === itemId) ?? null
      setSearch(item ? getDataToShow(item) : '')
      setDropdownOpen(open)
    },
    whileElementsMounted: autoUpdate,
    placement: isMobile ? 'bottom' : 'bottom-start',
    middleware: [
      offset(10),
      flip(),
      shift(),
      size(
        {
          apply: ({ availableWidth, rects, elements }) => {
            Object.assign(elements.floating.style, {
              width: isMobile ? `${availableWidth}px` : `${Math.max(rects.reference.width, 500)}px`,
              maxHeight: `${MAX_HEIGHT_DROPDOWN}px`
            })
          }
        },
        [isMobile]
      )
    ]
  })

  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const { renderItems, scrollRef, scrollToIndex } = useVirtualList({
    items: allItems,
    itemSize,
    overshootCount: 10,
    onScrollBottom: () => {
      hasNextPage && fetchNextPage()
    }
  })

  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown': {
        if (focusIndex === allItemsLength) {
          setFocusIndex(0)
          scrollToIndex(0)
          break
        }
        const newIndex = Math.min(focusIndex + 1, allItemsLength)
        setFocusIndex(newIndex)
        scrollToIndex(newIndex)
        break
      }
      case 'ArrowUp': {
        if (focusIndex === 0) {
          setFocusIndex(allItemsLength)
          scrollToIndex(allItemsLength)
          break
        }
        const newIndex = Math.max(focusIndex - 1, 0)
        setFocusIndex(newIndex)
        scrollToIndex(newIndex)
        break
      }
      case 'Enter': {
        const result = allItems[focusIndex]
        if (result) {
          handleItemClick(result)
          setFocusIndex(-1)
          setDropdownOpen(false)
        } else if (handleQuickCreate && focusIndex === allItemsLength) {
          handleQuickCreate(searchTerm, handleItemClick)
          setFocusIndex(-1)
          setDropdownOpen(false)
        }
        e.stopPropagation()
        e.preventDefault()
        break
      }
      default:
        break
    }
  }

  const onCreate: OnCreateType = async (data: any) => {
    setCreateModalOpen(false)
    await queryClient.invalidateQueries({ queryKey: getItemsQueryKeyFn() })
    onChange(data?.id)
    setImmediateSearchTerm(getDataToShow(data))
  }

  const onUpdate: OnCreateType = async (data: any) => {
    setUpdateModalOpen(false)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getItemQueryKeyFn(data?.id) }),
      queryClient.invalidateQueries({ queryKey: getItemsQueryKeyFn() })
    ])
    onChange(data?.id)
    setImmediateSearchTerm(getDataToShow(data))
  }

  const closeCreateModal = () => setCreateModalOpen(false)
  const closeUpdateModal = () => setUpdateModalOpen(false)

  const openCreateModal = () => {
    setDropdownOpen(false)
    setCreateModalOpen(true)
  }

  const handleOnEditClick = item => {
    onChange(item?.id)
    setDropdownOpen(false)
    setUpdateModalOpen(true)
  }

  const handleItemClick = item => {
    onChange(item?.id)
    setDropdownOpen(false)
    setImmediateSearchTerm(getDataToShow(item))
  }

  const handleFocus = () => {
    setDropdownOpen(true)
    setImmediateSearchTerm(result?.data ? getDataToShow(result.data) : '')
  }

  const renderModal = (
    renderForm,
    handleOnClose,
    rest,
    isVisible: boolean,
    modalType: 'new' | 'update'
  ) => {
    return (
      <ModalV2
        header={modalType === 'new' ? modalHeaderNew : modalHeaderUpdate}
        isVisible={isVisible}
        handleOnClose={() => handleOnClose(false)}
        footer={
          <>
            <Button onClick={() => handleOnClose(false)}>{t('general.cancel')}</Button>
            <Button
              type="submit"
              intent="success"
              form={formId}
              data-test="universal-api-selector-submit-form"
            >
              {t('general.save')}
            </Button>
          </>
        }
        {...rest}
      >
        {renderForm}
      </ModalV2>
    )
  }

  // Display searchTerm if dropdown is open, otherwise
  // default to item value. Fallback to searchTerm
  const displayValue = isDropdownOpen
    ? searchTerm
    : itemId && result?.data
    ? getDataToShow(result.data)
    : itemId
    ? searchTerm
    : ''

  // Show spinner if items are loading or search term has changed
  const showSpinner = isItemsLoading
  const showSecondarySpinner = isItemsFetching && !showSpinner

  return (
    <>
      {/* RENDER MODALS */}
      {renderCreateForm &&
        createModalProps &&
        renderModal(
          renderCreateForm(onCreate, undefined),
          closeCreateModal,
          createModalProps,
          isCreateModalOpen,
          'new'
        )}
      {result?.data &&
        renderUpdateForm &&
        updateModalProps &&
        renderModal(
          renderUpdateForm(onUpdate, result.data),
          closeUpdateModal,
          updateModalProps,
          isUpdateModalOpen,
          'update'
        )}

      {/* USER INPUT */}
      <div ref={refs.setReference} {...getReferenceProps()} style={{ width: '100%' }}>
        <Input
          data-test="universal-api-selector-v2-input"
          onKeyDown={onKeyDown}
          value={displayValue}
          onFocus={handleFocus}
          autoComplete="off"
          actionComponent={
            !!itemId && (
              <Button
                type="button"
                data-test="input-clear-selection"
                onClick={() => {
                  setImmediateSearchTerm('')
                  setDropdownOpen(false)
                  onChange(null)
                }}
                icon={<FaTimes></FaTimes>}
                isSecondary={true}
                style={{
                  padding: '0px',
                  paddingRight: `${theme.spacing.xxxs}rem`,
                  paddingLeft: `${theme.spacing.xs}rem`
                }}
              ></Button>
            )
          }
          onChange={e => {
            const searchTerm = e.currentTarget.value
            setSearch(searchTerm)
            setDropdownOpen(true)
            if (!searchTerm) {
              onChange(null)
            }
          }}
          {...rest}
        ></Input>
      </div>

      <input type="hidden" id={id} name={name} value={itemId ?? ''} />

      {/* DROP DOWN */}
      {isDropdownOpen && (
        <FloatingPortal root={rootRef}>
          <FloatingFocusManager
            context={context}
            returnFocus={false}
            modal={false}
            order={['reference', 'content']}
          >
            <StyledDropdown
              ref={refs.setFloating}
              style={{
                top: y || 0,
                left: x || 0,
                position: strategy
              }}
              {...getFloatingProps()}
            >
              <>
                <ActionBar>
                  {dropdownHeaderIcon && dropdownHeaderIcon}
                  <HeaderText>
                    <span>{dropDownHeader}</span> {showSecondarySpinner && <Spinner size={16} />}
                  </HeaderText>

                  {renderCreateForm && (
                    <Button
                      isSecondary={true}
                      icon={<FaPlus></FaPlus>}
                      onClick={() => openCreateModal()}
                      data-test="universal-api-selector-v2-add-new"
                    ></Button>
                  )}
                </ActionBar>

                <StyledDropdownScrollable ref={scrollRef}>
                  <AnimatedContentLoader
                    isLoading={showSpinner}
                    isEmpty={noResults && !useQuickAddItem}
                    isEmptyDescription={
                      renderEmpty ? renderEmpty({ openCreateModal }) : noDataMessage
                    }
                  >
                    {renderItems((item, style, index) =>
                      renderRow(item, {
                        isFocused: focusIndex === index,
                        isSelected: itemId === item.id,
                        handleOnItemClick: item => handleItemClick(item),
                        handleOnEditClick: item => handleOnEditClick(item),
                        style: style,
                        onKeyDown: onKeyDown,
                        showEditItem: renderUpdateForm !== undefined ? true : false,
                        isFirstOfType: index === 0,
                        isLastOfType: index === allItems.length - 1
                      })
                    )}
                    {useQuickAddItem &&
                      renderQuickCreate(searchTerm, focusIndex === allItemsLength, handleItemClick)}
                  </AnimatedContentLoader>
                </StyledDropdownScrollable>
              </>
            </StyledDropdown>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
      {isSuggestionEnabled &&
        suggestion &&
        renderSuggestion?.(suggestion, isSuggestionEnabled, {
          handleOnItemClick: suggestion => handleItemClick(suggestion)
        })}
    </>
  )
}

export default UniversalAPISelector

const StyledDropdownScrollable = styled.div`
  overflow: auto;
  flex: 1;
  padding-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const StyledDropdown = styled(motion.div).attrs({
  initial: { y: 10, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: 0, opacity: 0 }
})`
  display: flex;
  flex-direction: column;
  max-height: ${MAX_HEIGHT_DROPDOWN}px;
  background: white;
  z-index: 100000;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
`

const ActionBar = styled.div`
  display: flex;
  font-weight: 500;
  padding-left: ${({ theme }) => theme.spacing.sm}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  align-items: center;
  min-height: 40px;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const HeaderText = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
